import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Star, Trophy, AlertTriangle, CheckCircle, XCircle,
  FileDown, Users, Target, HelpCircle, MessageCircle, BarChart3, ArrowDown
} from "lucide-react";
import { API } from "../App";

const MhMComparisonReportPage = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [compReport, setCompReport] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const [sessRes, cfgRes] = await Promise.all([
          axios.get(`${API}/mhm/sessions/${sessionId}`),
          axios.get(`${API}/mhm/config`),
        ]);
        setSession(sessRes.data);
        setConfig(cfgRes.data);

        // Detect "no scored projects" early so we can show a friendly empty state
        // instead of firing a 400 from the generator.
        const scored = (sessRes.data.projects || []).filter((p) => p.fit_score != null);
        if (sessRes.data.comparison_report) {
          setCompReport(sessRes.data.comparison_report);
        } else if (scored.length === 0) {
          setError("no_projects");
        } else {
          const rep = await axios.post(`${API}/mhm/sessions/${sessionId}/generate-comparison-report`);
          setCompReport(rep.data);
        }

        const d = new Date(sessRes.data.created_at);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        document.title = `ProjectFitReport_${dd}_${mm}_${yy}`;
      } catch (err) {
        const detail = err.response?.data?.detail || "";
        if (detail.toLowerCase().includes("no scored projects")) {
          setError("no_projects");
        } else {
          setError(err.response?.status === 404 ? "Report not found" : "Failed to load");
        }
      } finally { setLoading(false); }
    };
    init();
    return () => { document.title = "PropertyCRM"; };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#87A96B] mx-auto" />
          <p className="mt-4 text-slate-500 text-sm">Generating your Project Fit Report...</p>
        </div>
      </div>
    );
  }

  // Friendly empty state — buyer reached this page but hasn't added/scored any
  // projects yet. Send them to the self-serve project compare page.
  if (error === "no_projects") {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4" data-testid="mhm-comparison-empty">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 rounded-2xl bg-[#F0F5EC] flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-[#87A96B]" />
          </div>
          <h2 className="text-lg font-bold text-[#2C3E50] mb-2">No homes compared yet</h2>
          <p className="text-sm text-[#6D6660] mb-5 leading-relaxed">
            Add the homes you're considering, rate each one against the priorities you just set, and we'll show you how they stack up.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <a
              href={`/mhm/projects/${sessionId}`}
              data-testid="mhm-comparison-add-projects"
              className="inline-flex items-center justify-center gap-2 bg-[#87A96B] hover:bg-[#76955d] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
            >
              Add & Compare Homes
            </a>
            <a
              href={`/mhm/report/${sessionId}`}
              data-testid="mhm-comparison-back-to-report"
              className="inline-flex items-center justify-center gap-2 bg-white border border-[#E8E3DE] hover:border-[#87A96B] text-[#2C3E50] px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
            >
              Back to my Clarity Report
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-[#87A96B]" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-2">Report Not Available</h2>
          <p className="text-sm text-slate-500 mb-4">This report link may have expired or the session no longer exists.</p>
          <a href="/mhm/start" className="inline-flex items-center gap-2 bg-[#87A96B] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
            Take MhM Assessment
          </a>
        </div>
      </div>
    );
  }

  const fp = session.fingerprint || {};
  const projects = (session.projects || []).filter((p) => p.fit_score != null).sort((a, b) => (b.fit_score || 0) - (a.fit_score || 0));
  const fMap = {};
  config?.factors?.forEach((f) => {
    const k = f.key || f.factor_id;
    fMap[k] = { business: f.businessLabel || f.name, buyer: f.buyerLabelV3 || f.buyerLabelV2 || f.buyerLabel || f.name };
  });
  const pMap = {};
  config?.personas?.forEach((p) => { pMap[p.key || p.persona_id] = p; });
  const persona = pMap[session.persona_id] || {};

  const topFactorNames = fp.top_factor_buyer_names || fp.top_factor_names || [];
  const reportId = `MHM-PF-${new Date(session.created_at).getFullYear()}-${sessionId.split("_")[1]?.slice(0, 6) || "000000"}`.toUpperCase();
  const dateStr = new Date(session.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  // Build comparison matrix data — only non-eliminated factors sorted by weight
  const matrixFactors = (fp.ranked_factors || []).filter((rf) => rf.weight > 0).slice(0, 10);

  const bandColor = (fk) => {
    if (session.eliminated_factors?.includes(fk)) return "text-red-400";
    if (session.high_priorities?.includes(fk)) return "text-[#5A7A4A] font-semibold";
    if (session.medium_priorities?.includes(fk)) return "text-orange-700";
    return "text-blue-600";
  };

  const scoreColor = (score) => {
    if (score >= 8) return "bg-[#E8F0E0] text-[#5A7A4A]";
    if (score >= 6) return "bg-[#F0F5EC] text-[#5A7A4A]";
    if (score >= 4) return "bg-[#FBF3EE] text-[#C4835A]";
    if (score >= 2) return "bg-[#FAF5F0] text-[#8B5E3A]";
    return "bg-slate-50 text-slate-400";
  };

  const fitColor = (score) => {
    if (score >= 75) return "text-[#87A96B] bg-[#F0F5EC] border-[#C8D8BC]";
    if (score >= 55) return "text-[#C4835A] bg-[#FBF3EE] border-[#E0C8B0]";
    return "text-[#8B5E3A] bg-[#FAF5F0] border-[#E0C8B0]";
  };

  return (
    <div className="min-h-screen bg-white" data-testid="mhm-comparison-report">
      {/* Print Button */}
      <div className="print:hidden fixed top-4 right-4 z-50">
        <button onClick={() => window.print()} className="bg-[#87A96B] text-white px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-1.5 hover:bg-indigo-700 transition-colors text-sm">
          <FileDown className="w-3.5 h-3.5" /> Download PDF
        </button>
      </div>

      <div className="max-w-[900px] mx-auto px-6 py-6 print:px-4 print:py-2 print:max-w-none">

        {/* ========== HEADER ========== */}
        <div className="flex items-start justify-between border-b-2 border-[#87A96B] pb-4 mb-5 rpt-no-break">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#87A96B] to-[#6B9155] flex items-center justify-center flex-shrink-0">
              <div className="text-white text-center leading-none">
                <p className="text-[7px] font-medium">My</p>
                <p className="text-sm font-black">MhM</p>
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">YOUR HOME COMPARISON REPORT</h1>
              <p className="text-sm text-slate-600 font-medium">How your shortlisted homes match your priorities.</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Based on your MyHomeMatrix home clarity profile.</p>
            </div>
          </div>
          <div className="text-right text-[10px] text-slate-500 space-y-0.5 flex-shrink-0 ml-4">
            <p><span className="font-semibold text-slate-700">REPORT ID</span> {reportId}</p>
            <p><span className="font-semibold text-slate-700">DATE</span> {dateStr}</p>
          </div>
        </div>

        {/* ========== SECTION 1: BUYER SNAPSHOT ========== */}
        <div className="bg-gradient-to-r from-[#F0F5EC] to-[#EAF0E4] rounded-xl p-4 mb-5 rpt-no-break">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#87A96B] flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-[#87A96B] font-semibold uppercase tracking-wider">Your Situation</p>
                <p className="text-base font-bold text-slate-900">{persona.buyerLabelV2 || persona.businessLabel || session.persona_id}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-[#87A96B]" />
              <div>
                <p className="text-[10px] text-slate-500">Top Priorities</p>
                <p className="text-xs font-semibold text-slate-800">{topFactorNames.slice(0, 3).join(", ")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#87A96B]" />
              <div>
                <p className="text-[10px] text-slate-500">Projects Compared</p>
                <p className="text-xs font-semibold text-slate-800">{projects.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ========== SECTION 2: PROJECT RANKINGS ========== */}
        <div className="mb-5 rpt-no-break">
          <h2 className="text-sm font-black text-slate-900 mb-3">1. OVERALL RANKINGS</h2>
          <div className="space-y-2">
            {projects.map((proj, idx) => (
              <div key={proj.project_id} className={`flex items-center gap-3 p-3 rounded-xl border-2 ${idx === 0 ? "border-[#C8D8BC] bg-[#F0F5EC]" : "border-slate-200"}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${idx === 0 ? "bg-[#87A96B] text-white" : idx === 1 ? "bg-slate-700 text-white" : "bg-slate-200 text-slate-600"}`}>
                  {idx === 0 ? <Trophy className="w-4 h-4" /> : <span className="font-bold text-sm">{idx + 1}</span>}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-900">{proj.name}</p>
                  {proj.location && <p className="text-[10px] text-slate-500">{proj.location}</p>}
                </div>
                <div className={`px-3 py-1.5 rounded-lg border text-center ${fitColor(proj.fit_score)}`}>
                  <p className="text-lg font-black">{proj.fit_score}%</p>
                  <p className="text-[9px]">fit score</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ========== SECTION 3: COMPARISON MATRIX ========== */}
        <div className="mb-5">
          <h2 className="text-sm font-black text-slate-900 mb-0.5 rpt-no-break">2. SIDE-BY-SIDE COMPARISON</h2>
          <p className="text-[10px] text-slate-400 mb-2">Factors ordered by your priority weight. Ratings are 0 (low) to 10 (high).</p>
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left px-2 py-1.5 font-semibold text-slate-600 w-[140px]">Factor (by weight)</th>
                  <th className="text-center px-1.5 py-1.5 font-semibold text-slate-600 w-[35px]">Wt</th>
                  {projects.map((p) => (
                    <th key={p.project_id} className="text-center px-2 py-1.5 font-semibold text-slate-800">{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrixFactors.map((rf, i) => (
                  <tr key={rf.factor_id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                    <td className={`px-2 py-1 ${bandColor(rf.factor_id)} text-[10px]`}>{fMap[rf.factor_id]?.buyer || rf.buyerLabel || rf.name}</td>
                    <td className="text-center px-1.5 py-1 text-slate-500 font-medium">{rf.weight}</td>
                    {projects.map((proj) => {
                      const score = proj.scores?.[rf.factor_id];
                      return (
                        <td key={proj.project_id} className="text-center px-2 py-1">
                          {score != null ? (
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${scoreColor(score)}`}>{score}</span>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-4 mt-1.5">
            {[{l: "8-10 Excellent", c: "bg-[#E8F0E0]"}, {l: "6-7 Good", c: "bg-[#F0F5EC]"}, {l: "4-5 Average", c: "bg-[#FBF3EE]"}, {l: "0-3 Weak", c: "bg-[#FAF5F0]"}].map((b) => (
              <div key={b.l} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded ${b.c} border`} />
                <span className="text-[9px] text-slate-500">{b.l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ========== SECTION 4: PER-PROJECT BREAKDOWN ========== */}
        <div className="mb-5 rpt-no-break">
          <h2 className="text-sm font-black text-slate-900 mb-3 rpt-no-break">3. WHAT STANDS OUT</h2>
          <div className="space-y-3">
            {projects.map((proj, idx) => {
              const aiProj = compReport?.per_project?.find((p) => p.name === proj.name) || compReport?.per_project?.[idx];
              return (
                <div key={proj.project_id} className="rounded-xl border p-4 rpt-no-break">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {idx === 0 && <Trophy className="w-4 h-4 text-[#87A96B]" />}
                      <h3 className="text-sm font-bold text-slate-900">{proj.name}</h3>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${fitColor(proj.fit_score)}`}>{proj.fit_score}%</span>
                    </div>
                  </div>
                  {proj.fit_details?.strength_names?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[10px] font-semibold text-[#5A7A4A] mb-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Where this home is strong</p>
                      <div className="flex flex-wrap gap-1">
                        {proj.fit_details.strength_names.map((s, i) => <span key={i} className="text-[10px] bg-[#F0F5EC] text-[#5A7A4A] border border-[#C8D8BC] rounded px-1.5 py-0.5">{s}</span>)}
                      </div>
                    </div>
                  )}
                  {proj.fit_details?.weakness_names?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[10px] font-semibold text-[#8B5E3A] mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Where it may fall short</p>
                      <div className="flex flex-wrap gap-1">
                        {proj.fit_details.weakness_names.map((w, i) => <span key={i} className="text-[10px] bg-[#FAF5F0] text-[#8B5E3A] border border-[#E0C8B0] rounded px-1.5 py-0.5">{w}</span>)}
                      </div>
                    </div>
                  )}
                  {aiProj && (
                    <>
                      {aiProj.strengths_text && <p className="text-[10px] text-slate-600 mb-1">{aiProj.strengths_text}</p>}
                      {aiProj.weaknesses_text && <p className="text-[10px] text-slate-500 mb-1">{aiProj.weaknesses_text}</p>}
                      {aiProj.verdict && <p className="text-[10px] font-semibold text-[#5B8A8A] bg-[#F0F5EC] rounded px-2 py-1 mt-1 inline-block">{aiProj.verdict}</p>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ========== SECTION 5: AI RECOMMENDATION ========== */}
        {compReport?.recommendation && (
          <div className="mb-5 rpt-no-break">
            <h2 className="text-sm font-black text-slate-900 mb-2">4. WHAT WE SUGGEST</h2>
            <div className="bg-gradient-to-r from-[#F0F5EC] to-[#EAF0E4] rounded-xl p-4 border border-[#D4E0CC]">
              {compReport.comparison_summary && <p className="text-[11px] text-slate-700 mb-3 leading-relaxed">{compReport.comparison_summary}</p>}
              <div className="bg-white rounded-lg p-3 border border-[#D4E0CC]">
                <div className="flex items-start gap-2">
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-[#4A4543] font-medium leading-relaxed">{compReport.recommendation}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== SECTION 6: QUESTIONS TO ASK ========== */}
        {compReport?.questions_to_ask?.length > 0 && (
          <div className="mb-5 rpt-no-break">
            <h2 className="text-sm font-black text-slate-900 mb-2">5. QUESTIONS TO ASK BEFORE DECIDING</h2>
            <p className="text-[10px] text-slate-500 mb-3">Use these during site visits or conversations with the project team.</p>
            <div className="space-y-3">
              {compReport.questions_to_ask.map((pq, i) => (
                <div key={i} className="rounded-lg border p-3 bg-slate-50/50">
                  <p className="text-[11px] font-bold text-slate-800 mb-2 flex items-center gap-1.5">
                    <HelpCircle className="w-3.5 h-3.5 text-[#87A96B]" /> {pq.project}
                  </p>
                  <ol className="space-y-1.5 ml-5">
                    {pq.questions?.map((q, j) => (
                      <li key={j} className="text-[10px] text-slate-700 list-decimal">{q}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========== FOOTER CTA ========== */}
        <div className="bg-gradient-to-r from-[#4A4543] to-[#5B5550] rounded-xl p-5 rpt-no-break print:hidden">
          <div className="flex items-center gap-3 mb-3">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400 flex-shrink-0" />
            <div>
              <p className="text-white text-sm font-bold">Your priorities are clear. Now choose with confidence.</p>
              <p className="text-[#C4B8AC] text-[11px]">Use this report to make a well-informed decision aligned with what truly matters to you.</p>
            </div>
          </div>
          <a href={`${window.location.origin}/mhm/projects/${sessionId}`} target="_blank" rel="noopener noreferrer"
            data-testid="mhm-comparison-manage-link"
            className="block bg-[#87A96B] hover:bg-[#76955d] rounded-lg p-4 text-center transition-colors mb-2"
            style={{ textDecoration: "none" }}>
            <p className="text-white text-sm font-bold">Add or manage homes</p>
            <p className="text-white/80 text-[10px] mt-1">Add more homes, edit ratings, and re-run the comparison</p>
          </a>
          <a href={`${window.location.origin}/mhm/next/${sessionId}`} target="_blank" rel="noopener noreferrer"
            data-testid="mhm-comparison-revisit-link"
            className="block bg-white/10 hover:bg-white/20 rounded-lg p-3 text-center transition-colors"
            style={{ textDecoration: "none" }}>
            <p className="text-white text-xs font-semibold">Other options — revisit your priorities or start fresh</p>
          </a>
        </div>

        <p className="text-center text-[8px] text-slate-300 mt-3">Powered by MyHomeMatrix | PropertyCRM</p>
      </div>

      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          @page { size: A4; margin: 10mm 12mm; }
          .rpt-no-break { page-break-inside: avoid; break-inside: avoid; }
          .rpt-page-break-before { page-break-before: always; break-before: page; }
        }
      `}</style>
    </div>
  );
};

export default MhMComparisonReportPage;
